import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import { FaBell, FaChevronLeft, FaInfoCircle, FaTrophy, FaBullhorn } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import '../scss/Notifications.scss';
import api from '../services/apiService';
import { achievements } from '../data/achievements';
import NotificationDetailModal from './NotificationDetailModal';
import { authService } from '../services/authService';
import {
  parseMatchIdFromNotification,
  stripMatchIdMarker,
  isAvailabilityNotification,
  isCoachNotificationView,
  isVisibleInCoachNotifications,
  notificationRequestIds,
  isVisibleForParentProfile,
  parseProductNewsContent,
  trackProductNewsClick,
  resolveAchievementIdFromMessage,
  formatAchievementLabel,
  lookupAchievement,
} from '../utils/notificationUtils';

// Create a map of achievement IDs to their details for quick lookup
const achievementsMap = achievements.reduce((map, achievement) => {
  map[achievement.id] = achievement;
  return map;
}, {});

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const navigate = useNavigate();
  const isCoachView = isCoachNotificationView();

  // Fetch notifications from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get the team_id and player_id from sessionStorage
        const teamId = sessionStorage.getItem('selected_team_id');
        const profileIds = notificationRequestIds();
        
        if (!teamId) {
          console.error('No team ID found in session storage');
          setNotifications([]);
          setIsLoading(false);
          return;
        }

        // Parents view a child, but payment notices are stored on the parent account.
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_announcements.php?team_id=${teamId}`;
        if (profileIds.requestPlayerId) {
          apiUrl += `&player_id=${profileIds.requestPlayerId}`;
        }
        if (isCoachView) {
          apiUrl += '&for_coach=1';
        }
        if (authService.isSuperAdmin()) {
          apiUrl += '&include_test_news=1';
        }
        apiUrl += '&categories=payment';

        // Fetch announcements from API with parameters
        const response = await api.get(apiUrl);
        
        if (response.data.success && response.data.announcements) {
          // Log the response to check is_read values
          console.log('API Response - Announcements:', response.data.announcements);
          
          // Process and format the announcements
          const formattedNotifications = response.data.announcements
            .filter((announcement) => isVisibleForParentProfile(announcement, profileIds))
            .map((announcement, index) => {
            // Extract a title from the message if it has a colon format
            const rawMessage = announcement.message || '';
            const message = stripMatchIdMarker(rawMessage);
            const matchId = parseMatchIdFromNotification(rawMessage, announcement.match_id);
            const colonIndex = message.indexOf(':');
            
            // Determine the notification type (if returned from API)
            const type = announcement.type || 'announcement';
            const playerName = (announcement.player_name || [announcement.first_name, announcement.last_name].filter(Boolean).join(' ')).trim();
            
            let title;
            let image = announcement.image_url || null;
            let content = message;

            let buttonLabel = null;
            let buttonUrl = null;
            let newsType = null;
            let newsId = null;

            if (type === 'product_news') {
              const productNews = parseProductNewsContent(announcement);
              title = productNews.title;
              content = productNews.content;
              image = productNews.image;
              buttonLabel = productNews.buttonLabel;
              buttonUrl = productNews.buttonUrl;
              newsType = productNews.newsType;
              newsId = productNews.newsId;
            } else if (type === 'mention') {
              title = 'Match Report Mention';
            } else if (type === 'achievement') {
              title = isCoachView && playerName
                ? playerName
                : 'Achievement Unlocked';
            } else if (type === 'last_match') {
              title = 'Match Update';
            } else if (type === 'payment') {
              if (colonIndex > 0) {
                title = message.substring(0, colonIndex).trim();
                content = message.substring(colonIndex + 1).trim();
              } else {
                title = 'Payment';
              }
            } else if (type === 'update') {
              title = 'Team Update';
            } else if (type === 'response') {
              title = 'Availability Update';
            } else if (colonIndex > 0) {
              title = message.substring(0, colonIndex).trim();
            } else {
              title = type === 'announcement' ? 'Announcement' : 'Notification';
            }
            
            // Parse date from created_at if available, otherwise use current date
            let timestamp;
            if (announcement.created_at) {
              try {
                timestamp = new Date(announcement.created_at);
                // If date is invalid, fall back to current date
                if (isNaN(timestamp.getTime())) {
                  timestamp = new Date();
                }
              } catch (error) {
                timestamp = new Date();
              }
            } else {
              timestamp = new Date();
            }
            
            const notificationItem = {
              id: announcement.notification_id || index + 1,
              type: type,
              title: title,
              content: content,
              message: type === 'product_news' ? content : message,
              image: image,
              buttonLabel: buttonLabel,
              buttonUrl: buttonUrl,
              newsType: newsType,
              newsId: newsId,
              playerName: playerName,
              timestamp: timestamp,
              isRead: announcement.is_read === "1" || announcement.is_read === true,
              isPlayerSpecific: announcement.target_type === 'player',
              player_id: announcement.player_id,
              previewOnly: !!announcement.preview_only,
              matchId: matchId,
              actions: [],
            };

            if (buttonUrl) {
              notificationItem.actions.push({
                label: buttonLabel || 'Open',
                action: 'openUrl',
                url: buttonUrl,
              });
            }

            if (type === 'payment') {
              notificationItem.actions.push({
                label: 'View payment',
                action: 'openPayment',
              });
            }

            if (matchId) {
              notificationItem.actions.push({
                label: isAvailabilityNotification(notificationItem)
                  ? 'Update availability'
                  : 'View match',
                action: 'viewMatch',
                matchId,
              });
            } else if (hasMatchInfo(message)) {
              notificationItem.actions.push({ label: 'View Match', action: 'viewMatch' });
            }

            return notificationItem;
          });

          const visibleNotifications = isCoachView
            ? formattedNotifications.filter(isVisibleInCoachNotifications)
            : formattedNotifications;

          setNotifications(visibleNotifications);
          
          // Dispatch event to update notification badge in the header
          window.dispatchEvent(new Event('notificationUpdate'));
        } else {
          console.error('Failed to fetch announcements:', response.data);
          setNotifications([]);
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
        setNotifications([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isCoachView]);

  // Filter notifications when the active filter or notifications change
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredNotifications(notifications);
    } else {
      setFilteredNotifications(
        notifications.filter(notification => {
          if (activeFilter === 'mentions') return notification.type === 'mention';
          if (activeFilter === 'achievements') return notification.type === 'achievement';
          if (activeFilter === 'updates') return notification.type === 'update' || notification.type === 'last_match';
          if (activeFilter === 'news') return notification.type === 'product_news';
          if (activeFilter === 'payment') return notification.type === 'payment';
          return true;
        })
      );
    }
  }, [activeFilter, notifications]);

  // Helper function to detect if a message contains match information
  const hasMatchInfo = (message) => {
    if (!message) return false;
    const lowerMessage = message.toLowerCase();
    return lowerMessage.includes('match') && 
           (lowerMessage.includes('pm') || lowerMessage.includes('am') || 
            lowerMessage.includes(':') || lowerMessage.includes('@'));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
    
    // Mark as read on server
    markNotificationAsRead(notification.id);

    if (notification.type === 'product_news' && notification.newsId) {
      trackProductNewsClick(notification.newsId, 'feed');
    }
    
    // Mark as read locally
    if (!notification.isRead) {
      console.log('Marking notification as read locally:', notification.id);
      
      const updatedNotifications = notifications.map(n => 
        n.id === notification.id ? { ...n, isRead: true } : n
      );
      setNotifications(updatedNotifications);
      
      // Also update filtered notifications
      setFilteredNotifications(prevFiltered => 
        prevFiltered.map(n => 
          n.id === notification.id ? { ...n, isRead: true } : n
        )
      );
    } else {
      console.log('Notification already marked as read:', notification.id);
    }
    
    // Show detail modal
    setSelectedNotification(notification);
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const formData = new FormData();
      formData.append('notification_id', notificationId);
      
      // Use the same base URL pattern as other API calls in the app
      let apiUrl = process.env.REACT_APP_API_URL;
      
      // Log the API endpoint for debugging
      console.log('Marking notification as read, API URL:', `${apiUrl}/mark_notification_read.php`);
      
      await api.post(`${apiUrl}/mark_notification_read.php`, formData);
      
      // Notify other components (like the Header) that a notification has been read
      window.dispatchEvent(new Event('notificationUpdate'));
      
      // No need to update UI here as we've already done it in handleNotificationClick
      console.log('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Continue showing the notification even if marking as read fails
    }
  };

  const closeModal = () => {
    setSelectedNotification(null);
  };

  const handleNotificationAction = (action, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (action.action === 'openPayment') {
      navigate('/membership');
      setSelectedNotification(null);
      return;
    }
    if (action.action === 'openUrl' && action.url) {
      if (selectedNotification?.newsId) {
        trackProductNewsClick(selectedNotification.newsId, 'cta');
      }
      window.open(action.url, '_blank', 'noopener,noreferrer');
      return;
    }
    const matchId = action.matchId || selectedNotification?.matchId;
    if (action.action === 'viewMatch' && matchId) {
      navigate(`/match/${matchId}`);
      setSelectedNotification(null);
    }
  };

  const formatNotificationContent = (notification) => {
    const message = notification.message || '';
    
    // Handle different notification types
    switch (notification.type) {
      case 'mention':
        // Check if the message has a player's name at the start (typically contains 'has been mentioned')
        if (message.includes('has been mentioned')) {
          // Extract the player name from the beginning of the message
          const hasBeenIndex = message.indexOf('has been');
          if (hasBeenIndex > 0) {
            let playerName = message.substring(0, hasBeenIndex).trim();
            
            // Check if player name contains an achievement
            const dashIndex = playerName.indexOf(' - ');
            if (dashIndex > 0) {
              // Not using achievementName for now
              // const achievementName = playerName.substring(dashIndex + 3);
              playerName = playerName.substring(0, dashIndex);
            }
            
            // Extract the match description (between "match report" and the final colon)
            const matchReportIndex = message.indexOf('match report');
            const finalColonIndex = message.lastIndexOf(':');
            
            let matchDescription = '';
            let comment = '';
            
            if (matchReportIndex > 0 && finalColonIndex > matchReportIndex) {
              matchDescription = message.substring(matchReportIndex + 13, finalColonIndex).trim();
              comment = message.substring(finalColonIndex + 1).trim();
              
              return (
                <>
                  <div className="match-description">{matchDescription}</div>
                  <div className="mention-comment">{comment}</div>
                </>
              );
            }
            
            // Fallback to the old format if we can't parse the new format
            const colonIndex = message.indexOf(':');
            if (colonIndex > 0) {
              const comment = message.substring(colonIndex + 1).trim();
              return (
                <>
                  <div className="mention-comment">{comment}</div>
                </>
              );
            }
          }
        }
        break;
        
      case 'product_news':
        return (
          <div className="product-news-notification">
            {notification.image && (
              <img src={notification.image} alt="" className="product-news-thumb" />
            )}
            <div className="product-news-text">{notification.content || message}</div>
          </div>
        );

      case 'achievement': {
        const achievementId = resolveAchievementIdFromMessage(message);
        const achievement = lookupAchievement(achievementsMap, achievementId);
        const achievementLabel = achievement?.title || formatAchievementLabel(achievementId);

        if (isCoachView) {
          return (
            <div className="achievement-notification">
              <div className="achievement-icon">
                {achievement?.icon || <FaTrophy />}
              </div>
              <div className="achievement-content">
                <div className="achievement-title">{achievementLabel}</div>
              </div>
            </div>
          );
        }

        if (achievement) {
          return (
            <div className="achievement-notification">
              <div className="achievement-icon">
                {achievement.icon || <FaTrophy />}
              </div>
              <div className="achievement-content">
                <div className="achievement-title">{achievement.title}</div>
                <div className="achievement-description">{achievement.description}</div>
              </div>
            </div>
          );
        }

        return (
          <div className="achievement-notification">
            <div className="achievement-icon">🏆</div>
            <div className="achievement-text">{achievementLabel}</div>
          </div>
        );
      }
        
      case 'last_match':
        // Try to extract match score or result if present in the message
        const matchResult = message.match(/\b\d+\s*-\s*\d+\b/); // Look for patterns like "2-1"
        if (matchResult) {
          return (
            <div className="match-notification">
              <div className="match-result">{matchResult[0]}</div>
              <div className="match-details">{message}</div>
            </div>
          );
        }
        break;
        
      case 'update':
        return (
          <div className="update-notification">
            <div className="update-text">{message}</div>
          </div>
        );
        
      default:
        // Default return the content as is
        return notification.content;
    }
    
    // Default return the content as is
    return notification.content;
  };

  const renderNotification = (notification) => (
    <div 
      key={notification.id} 
      className={`notification-item ${!notification.isRead ? 'unread' : ''} ${notification.isPlayerSpecific ? 'personal' : ''} ${notification.type === 'product_news' ? 'product-news-item' : ''}`}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="notification-icon">
        {notification.type === 'product_news' ? <FaBullhorn /> : <FaBell />}
      </div>
      <div className="notification-content">
        <div className="notification-header">
          <span className="title">{notification.title}</span>
          {notification.timestamp && (
            <span className="time">
              {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
            </span>
          )}
        </div>
        <div className={`notification-body${notification.type === 'payment' ? ' is-full' : ''}`}>
          {formatNotificationContent(notification)}
        </div>
        <div className="notification-actions">
          {notification.actions?.map((action, index) => (
            <button
              key={index}
              type="button"
              className="view-match-button"
              onClick={(e) => handleNotificationAction(action, e)}
            >
              {action.label}
            </button>
          ))}
          <button className="view-details-button">
            <FaInfoCircle /> {notification.isRead ? 'View Again' : 'View Details'}
          </button>
        </div>
      </div>
    </div>
  );

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const filterCounts = {
    all: notifications.length,
    mentions: notifications.filter((n) => n.type === 'mention').length,
    achievements: notifications.filter((n) => n.type === 'achievement').length,
    updates: notifications.filter((n) => n.type === 'update' || n.type === 'last_match').length,
    news: notifications.filter((n) => n.type === 'product_news').length,
    payment: notifications.filter((n) => n.type === 'payment').length,
  };

  return (
    <div className="notifications-page">
      <Header />
      <div className="notifications-container">
        <div className="sidebar">
          <div className="notifications-header">
            <button className="back-button" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
            <h1>Notifications</h1>
          </div>
        </div>

        <div className="main-content">
          <div className="notification-filters">
            <button 
              className={`filter-button ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All <span className="filter-count">{filterCounts.all}</span>
            </button>
            {!isCoachView && (
              <button 
                className={`filter-button ${activeFilter === 'mentions' ? 'active' : ''}`}
                onClick={() => handleFilterChange('mentions')}
              >
                Mentions <span className="filter-count">{filterCounts.mentions}</span>
              </button>
            )}
            <button 
              className={`filter-button ${activeFilter === 'achievements' ? 'active' : ''}`}
              onClick={() => handleFilterChange('achievements')}
            >
              Achievements <span className="filter-count">{filterCounts.achievements}</span>
            </button>
            <button
              className={`filter-button ${activeFilter === 'payment' ? 'active' : ''}`}
              onClick={() => handleFilterChange('payment')}
            >
              Payment <span className="filter-count">{filterCounts.payment}</span>
            </button>
            {isCoachView ? (
              <button 
                className={`filter-button ${activeFilter === 'news' ? 'active' : ''}`}
                onClick={() => handleFilterChange('news')}
              >
                News <span className="filter-count">{filterCounts.news}</span>
              </button>
            ) : (
              <button 
                className={`filter-button ${activeFilter === 'updates' ? 'active' : ''}`}
                onClick={() => handleFilterChange('updates')}
              >
                Updates <span className="filter-count">{filterCounts.updates}</span>
              </button>
            )}
          </div>
          
          <div className="content-area">
            {isLoading ? (
              <div className="loading">Loading...</div>
            ) : filteredNotifications.length > 0 ? (
              <div className="notifications-list">
                {filteredNotifications.map(renderNotification)}
              </div>
            ) : (
              <div className="no-content">
                <FaBell className="icon" />
                <p>No notifications{activeFilter !== 'all' ? ' of this type' : ''} yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          onClose={closeModal}
          onAction={handleNotificationAction}
        />
      )}
    </div>
  );
};

export default Notifications;